import {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  ComponentRef,
} from 'react';
import { StackNavigationProp, useNavigation } from '@bonanza-social/navigation';
import { TextInput } from 'react-native';
import { useDispatch, useSelector } from '@bonanza-social/toolkit/state';
import {
  selectRegistrationLimits,
  updateRegistrationData,
} from '../../../state';
import { LimitsErrorsType } from './RegistrationLimitsModal.types';
import { RegistrationLimitSchema } from '../../../state/registrationForm/registrationForm.schema';
import { set } from 'lodash-es';
import {
  Box,
  Button,
  Input,
  KeyboardShift,
  KeyboardSpacer,
  Text,
} from '@bonanza-social/moonlight';

function RegistrationLimitsModal() {
  const dispatch = useDispatch();
  const limits = useSelector(selectRegistrationLimits);
  const navigation =
    useNavigation<StackNavigationProp<AppNavigatorParamsList>>();
  const [errors, setErrors] = useState<LimitsErrorsType>({});
  const [data, setData] = useState(limits);
  const [showErrors, setShowErrors] = useState(false);
  const [hasScrolledForErrors, setHasScrolledForErrors] = useState(false);
  const hasErrors = useMemo(() => Object.keys(errors).length > 0, [errors]);
  const scrollViewRef = useRef<ComponentRef<typeof KeyboardShift>>(null);

  const fieldSequence = useMemo(
    () => [
      { type: 'Loss', period: 'Daily' },
      { type: 'Loss', period: 'Weekly' },
      { type: 'Loss', period: 'Monthly' },
    ],
    [],
  );

  /**
   * Focuses the input with numeric keyboard won't work
   * until we update our version. It's a known bug
   * that has a potential fix merged according to
   * this gitHub https://github.com/facebook/react-native/issues/48259
   */
  const inputRefs = useRef<Record<string, TextInput | null>>({});

  const registerInputRef = useCallback(
    (type: string, period: string, ref: TextInput | null) => {
      const key = `${type}.${period}`;
      inputRefs.current[key] = ref;
    },
    [],
  );

  const handleSubmitEditing = useCallback(
    (currentType: string, currentPeriod: string) => {
      const currentIndex = fieldSequence.findIndex(
        (item) => item.type === currentType && item.period === currentPeriod,
      );

      if (currentIndex < fieldSequence.length - 1) {
        const nextField = fieldSequence[currentIndex + 1];
        const nextKey = `${nextField.type}.${nextField.period}`;
        inputRefs.current[nextKey]?.focus();
      }
    },
    [fieldSequence],
  );

  const updateData = useCallback(
    (type: 'Loss', period: string, value: string) => {
      const parsedValue = value.replace(/[^0-9.]/g, '');
      const numberValue = parsedValue ? +parsedValue : undefined;

      const limits = {
        ...data,
        [type]: {
          ...data[type],
          [period]: numberValue,
        },
      };

      setData(limits);
    },
    [data],
  );

  const handleClose = useCallback(() => {
    navigation.goBack();
  }, [navigation]);

  const handleConfirm = useCallback(() => {
    if (hasErrors) {
      if (!showErrors) {
        setShowErrors(true);
        setHasScrolledForErrors(false);
      }
      return;
    }

    dispatch(updateRegistrationData({ limits: data }));

    handleClose();
  }, [hasErrors, showErrors, data, handleClose, dispatch]);

  useEffect(() => {
    const { error } = RegistrationLimitSchema.safeParse(data);

    const issues = error?.issues || [];

    const mappedErrors = issues.reduce(
      (acc, val) => set(acc, val.path.join('.'), val.message),
      {},
    );

    setErrors(mappedErrors);
  }, [data]);

  useEffect(() => {
    if (showErrors && hasErrors && !hasScrolledForErrors) {
      setTimeout(() => {
        scrollViewRef.current?.scrollToEnd({ animated: true });
        setHasScrolledForErrors(true);
      }, 100);
    }
  }, [showErrors, hasErrors, hasScrolledForErrors]);

  useEffect(() => {
    if (!showErrors) {
      setHasScrolledForErrors(false);
    }
  }, [showErrors]);

  return (
    <Box flex={1}>
      <KeyboardShift
        ref={scrollViewRef}
        addSpace={false}
        isInsideTabBar={false}
        additionalOffset={100}
        contentViewStyle={{ height: '100%' }}
        contentContainerStyle={{
          gap: 'space-400',
          padding: 'space-200',
        }}
      >
        <Box gap="space-400">
          <Box gap="space-200" testID="limits">
            <Input
              placeholderNS="registration"
              placeholderKey="dailyLimits"
              keyboardType="numeric"
              enterKeyHint="next"
            />
            <Input
              placeholderNS="registration"
              placeholderKey="weeklyLimits"
              keyboardType="numeric"
              enterKeyHint="next"
            />
          </Box>

          <Box gap="space-200" testID="loss-limits">
            <Text
              variant="h6"
              gradient="darkSurfaceAccent"
              marginBottom="space-200"
              locNS="registration"
              locKey="lossLimitsTitle"
            ></Text>
            <Input
              ref={(ref) => registerInputRef('Loss', 'Daily', ref)}
              placeholderNS="registration"
              placeholderKey="dailyLimits"
              keyboardType="numeric"
              enterKeyHint="next"
              value={data.Loss.Daily?.toString() || ''}
              onChangeText={(value) => updateData('Loss', 'Daily', value)}
              onSubmitEditing={() => handleSubmitEditing('Loss', 'Daily')}
              error={showErrors ? errors.Loss?.Daily : undefined}
            />
            {showErrors && errors.Loss?.Daily && (
              <Text color="darkSurfaceError" variant="body-m">
                {errors.Loss?.Daily}
              </Text>
            )}
            <Input
              ref={(ref) => registerInputRef('Loss', 'Weekly', ref)}
              placeholderNS="registration"
              placeholderKey="weeklyLimits"
              keyboardType="numeric"
              enterKeyHint="next"
              value={data.Loss.Weekly?.toString() || ''}
              onChangeText={(value) => updateData('Loss', 'Weekly', value)}
              onSubmitEditing={() => handleSubmitEditing('Loss', 'Weekly')}
              error={showErrors ? errors.Loss?.Weekly : undefined}
            />
            {showErrors && errors.Loss?.Weekly && (
              <Text color="darkSurfaceError" variant="body-m">
                {errors.Loss?.Weekly}
              </Text>
            )}
            <Input
              ref={(ref) => registerInputRef('Loss', 'Monthly', ref)}
              placeholderNS="registration"
              placeholderKey="monthlyLimits"
              keyboardType="numeric"
              enterKeyHint="go"
              value={data.Loss.Monthly?.toString() || ''}
              onChangeText={(value) => updateData('Loss', 'Monthly', value)}
              onSubmitEditing={() => handleSubmitEditing('Loss', 'Monthly')}
              error={showErrors ? errors.Loss?.Monthly : undefined}
            />
            {showErrors && errors.Loss?.Monthly && (
              <Text color="darkSurfaceError" variant="body-m">
                {errors.Loss?.Monthly}
              </Text>
            )}
          </Box>
        </Box>
      </KeyboardShift>
      <Box
        gap="space-400"
        marginHorizontal="space-200"
        marginVertical="space-200"
      >
        <Button
          variant="primary"
          locNS="registration"
          locKey="saveLimitsButton"
          size="large"
          onPress={handleConfirm}
        />
        <Button
          variant="secondary"
          locNS="registration"
          locKey="closeLimitsButton"
          size="large"
          onPress={handleClose}
        />
      </Box>

      <KeyboardSpacer paddingBottom="safe-area-inset-bottom" />
    </Box>
  );
}
export default memo(RegistrationLimitsModal);
